import React from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, Check, X, ExternalLink, Dumbbell, GraduationCap, Home, Users, MapPin } from "lucide-react";
import { useLang, UI } from "../i18n";
import { getProject, byCategory, FICHE, TRAVEL } from "../content";
import { useLenis, Reveal } from "../components/motion";
import { MiniSite, CourtLines, MonoTag, GhostText, Stamp, PlaneIcon } from "../components/motifs";
import { FicheInfo, FaqBlock } from "../components/Fiche";
import { WhatsAppShare } from "../components/Lightbox";
import { ContactSection } from "../components/Layout";

const CTA_LABEL = {
  immersions: { fr: "Réserver une immersion", en: "Book an immersion", es: "Reservar una inmersión" },
  evenements: { fr: "Postuler au Tryout", en: "Apply for a Tryout", es: "Postular al Tryout" },
};

const ProjectHero = ({ p, th, category }) => {
  const { t } = useLang();
  const backTo = category === "programmes" ? "/programmes" : "/immersions";
  return (
    <section className="relative pt-28 md:pt-40 pb-14 md:pb-20 overflow-hidden grain">
      <GhostText text={p.name.toUpperCase()} className="text-[13vw] top-24 -left-2" />
      <CourtLines className="-right-56 -top-40 w-[640px] h-[640px]" />
      <div className="relative z-10 max-w-[1400px] mx-auto px-5 md:px-10">
        <Link to={backTo} data-testid="project-back"
          className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white transition-colors duration-200">
          <ArrowLeft size={13} /> {t(UI.nav[category])}
        </Link>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center mt-8">
          <div className="lg:col-span-7">
            <div className="flex flex-wrap items-center gap-3">
              <MonoTag className="border border-white/25 rounded-full px-3 py-1.5 text-white/70">{p.code}</MonoTag>
              <MonoTag style={{ color: th.accent }}>{t(p.tag)}</MonoTag>
              {p.badge && (
                <MonoTag data-testid="project-badge" className="rounded-full px-3 py-1.5 text-black font-semibold"
                  style={{ backgroundColor: th.accent }}>
                  {t(p.badge)}
                </MonoTag>
              )}
              {p.comingSoon && (
                <MonoTag className="blink border rounded-full px-3 py-1.5" style={{ color: th.accent, borderColor: `${th.accent}66` }}>
                  {t({ fr: "Ouverture prochaine", en: "Opening soon", es: "Próxima apertura" })}
                </MonoTag>
              )}
            </div>
            <h1 className="font-display uppercase leading-[0.85] text-5xl sm:text-6xl lg:text-8xl tracking-tight mt-5">{p.name}</h1>
            <p className="font-mono text-[10px] md:text-[11px] uppercase tracking-[0.25em] text-white/60 mt-5">{p.city}</p>
            {p.flight && (
              <div className="flex items-center gap-4 font-display text-3xl md:text-4xl mt-6">
                <span>{p.flight.from}</span>
                <span className="relative w-24 border-t-2 border-dashed border-white/30">
                  <PlaneIcon size={17} className="absolute -top-2.5 left-1/2 -translate-x-1/2 rotate-90" style={{ color: th.accent }} />
                </span>
                <span style={{ color: th.accent }}>{p.flight.to}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/50 ml-3">
                  {t(TRAVEL.gate)} {p.flight.gate} · {t(TRAVEL.seat)} {p.flight.seat}
                </span>
              </div>
            )}
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <a href="#contact" data-testid="project-apply"
                className="inline-flex items-center gap-2 rounded-full px-8 py-4 font-heading font-semibold uppercase text-sm tracking-wide text-black hover:opacity-85 transition-opacity duration-300"
                style={{ backgroundColor: th.accent }}>
                {t(p.cta || CTA_LABEL[category] || FICHE.apply)} <ArrowUpRight size={18} />
              </a>
              <WhatsAppShare testid="project-share" text={`${p.name} — Air Sports Travel`}
                label={t({ fr: "Partager", en: "Share", es: "Compartir" })} />
            </div>
          </div>
          <Reveal className="lg:col-span-5 relative">
            <div className="relative overflow-hidden rounded-lg">
              <img src={p.img} alt={p.name} className="w-full h-[340px] md:h-[440px] object-cover" />
              <div className="absolute inset-0" style={{ background: `linear-gradient(to top, ${th.bg}cc, transparent 60%)` }} />
              <Stamp top={p.city} bottom={p.code} className="absolute bottom-5 right-5" color={th.accent} />
            </div>
            {p.logo && (
              <img src={p.logo} alt={p.name} data-testid="project-logo"
                className="absolute -bottom-6 -left-4 h-24 w-24 object-contain rounded-xl bg-white p-1.5 shadow-2xl" />
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const ObjectivesSection = ({ p, th }) => {
  const { t } = useLang();
  return (
    <section className="relative overflow-hidden pb-20 md:pb-28">
      <CourtLines className="-left-56 bottom-0 w-[560px] h-[560px]" opacity={0.05} />
      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12">
        <Reveal>
          <MonoTag style={{ color: th.accent }}>{t(FICHE.about)}</MonoTag>
          <h2 className="font-display text-3xl md:text-5xl uppercase leading-none mt-3">{t(p.tag)}</h2>
          <p className="font-body text-white/75 mt-6 text-base md:text-lg leading-relaxed">{t(p.desc)}</p>
        </Reveal>
        <Reveal delay={0.1}>
          <MonoTag style={{ color: th.accent }}>{t(FICHE.objectives)}</MonoTag>
          <ul className="mt-6 space-y-4" data-testid="project-objectives">
            {p.objectives.map((o) => (
              <li key={o.fr} className="flex gap-4 items-start rounded-md p-5 border border-white/10"
                style={{ backgroundColor: "var(--ms-surface)" }}>
                <span className="mt-0.5 rounded-full p-1" style={{ backgroundColor: th.accent }}>
                  <Check size={14} className="text-black" />
                </span>
                <span className="font-body text-white/85">{t(o)}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </div>
    </section>
  );
};

const CheckList = ({ items, accent }) => {
  const { t } = useLang();
  return (
    <ul className="mt-5 space-y-3">
      {items.map((it) => (
        <li key={it.fr} className="flex gap-3 items-start">
          <Check size={16} className="mt-1 shrink-0" style={{ color: accent }} />
          <span className="font-body text-sm text-white/85">{t(it)}</span>
        </li>
      ))}
    </ul>
  );
};

const SportCard = ({ p, th }) => {
  const { t } = useLang();
  return (
    <Reveal>
      <div data-testid="detail-sport" className="h-full rounded-md border border-white/10 p-8"
        style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${th.accent}` }}>
        <Dumbbell size={26} style={{ color: th.accent }} />
        <h3 className="font-display text-2xl uppercase mt-4">{t(FICHE.sport)}</h3>
        <CheckList items={p.sportsProgram} accent={th.accent} />
        {p.facilities && (
          <div className="mt-6 pt-5 border-t border-white/10" data-testid="detail-facilities">
            <div className="flex items-center gap-2">
              <MapPin size={18} style={{ color: th.accent }} />
              <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-white/60">{t(FICHE.facilities)}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {p.facilities.map((f) => (
                <span key={f.fr} className="font-heading text-xs uppercase tracking-wide border border-white/20 rounded-full px-4 py-2">
                  {t(f)}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </Reveal>
  );
};

const AcademicCard = ({ p, th }) => {
  const { t } = useLang();
  return (
    <Reveal delay={0.05}>
      <div data-testid="detail-academic" className="h-full rounded-md border border-white/10 p-8"
        style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${th.accent}` }}>
        <GraduationCap size={26} style={{ color: th.accent }} />
        <h3 className="font-display text-2xl uppercase mt-4">{t(FICHE.academicT)}</h3>
        {p.academicItems && <CheckList items={p.academicItems} accent={th.accent} />}
        {p.academicPartners && (
          <div className="mt-5 space-y-3">
            {p.academicPartners.map((pt, i) => (
              <a key={pt.name} href={pt.url} target="_blank" rel="noopener noreferrer" data-testid={`partner-link-${i}`}
                className="group/p block rounded-md border border-white/15 bg-black/25 p-4 hover:border-white/40 transition-colors duration-200">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-heading font-semibold" style={{ color: th.accent }}>{pt.name}</span>
                  <ExternalLink size={15} className="text-white/40 group-hover/p:text-white transition-colors duration-200" />
                </div>
                <p className="font-body text-xs text-white/65 mt-2">{t(pt.desc)}</p>
              </a>
            ))}
          </div>
        )}
      </div>
    </Reveal>
  );
};

const LogisticsCard = ({ p, th }) => {
  const { t } = useLang();
  return (
    <Reveal delay={0.1}>
      <div data-testid="detail-logistics" className="h-full rounded-md border border-white/10 p-8"
        style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${th.accent}` }}>
        <Home size={26} style={{ color: th.accent }} />
        <h3 className="font-display text-2xl uppercase mt-4">{t(FICHE.logistics)}</h3>
        <ul className="mt-5 space-y-3">
          {(p.inclusions || []).map((it) => (
            <li key={it.fr} className="flex gap-3 items-start">
              <Check size={16} className="mt-1 shrink-0 text-emerald-400" />
              <span className="font-body text-sm text-white/85">{t(it)}</span>
            </li>
          ))}
          {(p.exclusions || []).map((it) => (
            <li key={`x-${it.fr}`} className="flex gap-3 items-start">
              <X size={16} className="mt-1 shrink-0 text-red-400" />
              <span className="font-body text-sm text-white/60">{t(FICHE.notIncluded)} : {t(it)}</span>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
};

const StaffCard = ({ p, th }) => {
  const { t } = useLang();
  const withPhoto = p.staff.filter((s) => s.photo);
  const withoutPhoto = p.staff.filter((s) => !s.photo);
  return (
    <Reveal delay={0.15}>
      <div data-testid="detail-staff" className="h-full rounded-md border border-white/10 p-8"
        style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${th.accent}` }}>
        <Users size={26} style={{ color: th.accent }} />
        <h3 className="font-display text-2xl uppercase mt-4">{t(FICHE.staffT)}</h3>
        {withPhoto.length > 0 && (
          <div className="grid grid-cols-3 gap-3 mt-6" data-testid="staff-photo-grid">
            {withPhoto.map((s, i) => (
              <div key={s.name} data-testid={`staff-photo-${i}`} className="text-center">
                <div className="overflow-hidden rounded-md border border-white/15">
                  <img src={s.photo} alt={s.name} className="w-full aspect-[3/4] object-cover" />
                </div>
                <p className="font-heading font-semibold text-sm mt-3 leading-tight">{s.name}</p>
                <p className="font-mono text-[9px] uppercase tracking-[0.15em] mt-1" style={{ color: th.accent }}>{t(s.role)}</p>
              </div>
            ))}
          </div>
        )}
        <ul className="mt-6 space-y-4">
          {withoutPhoto.map((s) => (
            <li key={s.name} className="border-l-2 pl-4" style={{ borderColor: th.accent }}>
              <p className="font-heading font-semibold">{s.name}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/50 mt-1">{t(s.role)}</p>
            </li>
          ))}
        </ul>
      </div>
    </Reveal>
  );
};

const DetailsGrid = ({ p, th }) => {
  if (!(p.sportsProgram || p.academicPartners || p.inclusions || p.staff)) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28 grid grid-cols-1 lg:grid-cols-2 gap-6"
      data-testid="project-details">
      {p.sportsProgram && <SportCard p={p} th={th} />}
      {(p.academicPartners || p.academicItems) && <AcademicCard p={p} th={th} />}
      {(p.inclusions || p.exclusions) && <LogisticsCard p={p} th={th} />}
      {p.staff && <StaffCard p={p} th={th} />}
    </section>
  );
};

const DestinationsSection = ({ p, th }) => {
  const { t } = useLang();
  if (!p.destinations) return null;
  const hasPrice = p.destinations.some((d) => d.price);
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="project-destinations">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>
          {t({ fr: "Planning des voyages · Saison 2026 → 2027", en: "Travel schedule · 2026-27 season", es: "Calendario de viajes · Temporada 2026-27" })}
        </MonoTag>
        <div className="mt-5 rounded-md border border-white/10 overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 font-mono text-[9px] uppercase tracking-[0.25em] text-white/50 border-b border-white/10"
            style={{ backgroundColor: "var(--ms-surface)" }}>
            <span className={hasPrice ? "col-span-4" : "col-span-5"}>{t({ fr: "Période", en: "Period", es: "Período" })}</span>
            <span className={hasPrice ? "col-span-5" : "col-span-7"}>{t(TRAVEL.destination)}</span>
            {hasPrice && <span className="col-span-3 text-right">{t(FICHE.price)}</span>}
          </div>
          {p.destinations.map((d, i) => (
            <div key={`${d.period.fr}-${d.place.fr}`} data-testid={`destination-row-${i}`}
              className={`grid grid-cols-1 md:grid-cols-12 gap-1 md:gap-4 px-6 py-5 border-b border-white/10 last:border-b-0 ${d.tbd ? "opacity-60" : ""}`}
              style={{ backgroundColor: i % 2 === 0 ? "transparent" : "var(--ms-surface)" }}>
              <span className={`${hasPrice ? "md:col-span-4" : "md:col-span-5"} font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 self-center`}>{t(d.period)}</span>
              <span className={`${hasPrice ? "md:col-span-5" : "md:col-span-7"} flex items-center gap-3 font-heading font-semibold self-center`}>
                <PlaneIcon size={14} className="rotate-45 shrink-0" style={{ color: th.accent }} />
                {t(d.place)}
              </span>
              {d.price && (
                <span className="md:col-span-3 md:text-right font-heading font-semibold self-center" style={{ color: d.tbd ? undefined : th.accent }}>
                  {t(d.price)}
                </span>
              )}
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const VideoSection = ({ p, th }) => {
  const { t } = useLang();
  if (!p.video) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="project-video">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>{t({ fr: "La vidéo du programme", en: "Program video", es: "Vídeo del programa" })}</MonoTag>
        <div className={`mt-5 relative overflow-hidden rounded-lg border border-white/10 ${p.videoPortrait ? "w-full max-w-[380px]" : "w-full"}`}
          style={{ aspectRatio: p.videoPortrait ? "9 / 16" : "16 / 9" }}>
          <iframe src={p.video} title={`${p.name} — video`} data-testid="project-video-iframe"
            className="absolute inset-0 w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen />
        </div>
      </Reveal>
    </section>
  );
};

const GallerySection = ({ p, th }) => {
  const { t } = useLang();
  if (!p.gallery) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="project-gallery">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>{t({ fr: "Galerie", en: "Gallery", es: "Galería" })}</MonoTag>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:auto-rows-[260px]">
          {p.gallery.map((g, i) => (
            <figure key={g.src} data-testid={`gallery-item-${i}`}
              className={`group relative overflow-hidden rounded-md border border-white/10 ${i === 0 && p.gallery.length > 2 ? "sm:col-span-2 lg:col-span-2 lg:row-span-2" : ""} ${i === p.gallery.length - 1 && p.gallery.length % 2 === 1 ? "lg:col-span-2" : ""}`}>
              <img src={g.src} alt={t(g.cap)} loading="lazy"
                className={`w-full object-cover ${g.pos === "top" ? "object-top" : ""} transition-transform duration-500 group-hover:scale-105 lg:h-full ${i === 0 && p.gallery.length > 2 ? "h-[300px] sm:h-[420px]" : "h-[240px]"}`} />
              <figcaption className="absolute bottom-0 inset-x-0 px-4 py-3 font-mono text-[9px] uppercase tracking-[0.2em] text-white/85"
                style={{ background: `linear-gradient(to top, ${th.bg}e6, transparent)` }}>
                {t(g.cap)}
              </figcaption>
            </figure>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const PriceTableSection = ({ p, th }) => {
  const { t } = useLang();
  if (!p.priceTable) return null;
  const pt = p.priceTable;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="project-pricetable">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>{t(pt.title)}</MonoTag>
        <div className="mt-5 rounded-md border border-white/10 overflow-hidden">
          <div className="grid grid-cols-3 gap-4 px-6 py-3 font-mono text-[9px] uppercase tracking-[0.25em] border-b border-white/10"
            style={{ backgroundColor: "var(--ms-surface)" }}>
            {pt.headers.map((h, i) => (
              <span key={h.fr} className={i > 0 ? "text-right" : ""}>{t(h)}</span>
            ))}
          </div>
          {pt.rows.map((r, i) => (
            <div key={r.d.fr} data-testid={`price-row-${i}`}
              className="grid grid-cols-3 gap-4 px-6 py-4 border-b border-white/10 last:border-b-0"
              style={{ backgroundColor: i % 2 === 0 ? "transparent" : "var(--ms-surface)" }}>
              <span className="font-heading font-semibold text-sm self-center">{t(r.d)}</span>
              <span className="font-display text-xl md:text-2xl text-right self-center" style={{ color: th.accent }}>{r.reduced}</span>
              <span className="font-heading font-semibold text-right self-center">{r.standard}</span>
            </div>
          ))}
        </div>
        {pt.note && (
          <p data-testid="pricetable-note" className="mt-5 rounded-md border border-white/10 p-5 font-mono text-[10px] uppercase tracking-[0.08em] leading-relaxed"
            style={{ backgroundColor: "var(--ms-surface)" }}>
            {t(pt.note)}
          </p>
        )}
      </Reveal>
    </section>
  );
};

const StoriesSection = ({ p, th }) => {
  const { t } = useLang();
  if (!p.stories) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="project-stories">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>
          ★ {t({ fr: "Ils ont fait la ligue d'été · Et après ?", en: "They did the summer league · What next?", es: "Hicieron la liga de verano · ¿Y después?" })} ★
        </MonoTag>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          {p.stories.map((s, i) => (
            <div key={s.name} data-testid={`story-card-${i}`} className="flex gap-5 rounded-md border border-white/10 p-6"
              style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${th.accent}` }}>
              <img src={s.photo} alt={s.name} className="w-[110px] h-[150px] object-cover object-top rounded-md shrink-0" />
              <div>
                <h3 className="font-display text-2xl uppercase">{s.name}</h3>
                <p className="font-body text-sm leading-relaxed mt-3">{t(s.text)}</p>
              </div>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const FormulesSection = ({ p, th }) => {
  const { t } = useLang();
  if (!p.formules) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="project-formules">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>{t({ fr: "Formules & Tarifs", en: "Packages & Rates", es: "Fórmulas y Tarifas" })}</MonoTag>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-6">
          {p.formules.map((f, i) => (
            <div key={f.price} data-testid={`formule-${i}`} className="rounded-md border border-white/10 p-8"
              style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${th.accent}` }}>
              <h3 className="font-display text-2xl uppercase">{t(f.name)}</h3>
              <p className="font-display text-5xl mt-3" style={{ color: th.accent }}>{f.price}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-white/55 mt-4 leading-relaxed">{t(f.note)}</p>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const NbaSection = ({ p, th }) => {
  const { t } = useLang();
  if (!p.nba) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="project-nba">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>{t({ fr: "Expérience NBA", en: "NBA Experience", es: "Experiencia NBA" })}</MonoTag>
        <p className="font-body text-sm italic text-white/60 mt-4 max-w-2xl">{t(p.nba.note)}</p>
        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          {p.nba.games.map((g) => (
            <div key={g.game + g.date.fr} className="flex items-center gap-4 rounded-md border border-white/10 border-l-4 px-5 py-4"
              style={{ backgroundColor: "var(--ms-surface)", borderLeftColor: th.accent }}>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] shrink-0" style={{ color: th.accent }}>{t(g.date)}</span>
              <span className="font-heading font-semibold text-sm">{g.game}</span>
            </div>
          ))}
        </div>
      </Reveal>
    </section>
  );
};

const VisitsSection = ({ p, th }) => {
  const { t } = useLang();
  if (!p.visits) return null;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28" data-testid="project-visits">
      <Reveal>
        <MonoTag style={{ color: th.accent }}>{t({ fr: "Visites & Activités (exemples)", en: "Visits & Activities (examples)", es: "Visitas y Actividades (ejemplos)" })}</MonoTag>
        <p className="font-body text-sm italic text-white/60 mt-4 max-w-2xl">{t(p.visits.note)}</p>
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {p.visits.groups.map((gr) => (
            <div key={gr.title.fr} className="rounded-md border border-white/10 p-8"
              style={{ backgroundColor: "var(--ms-surface)" }}>
              <h3 className="font-display text-2xl uppercase" style={{ color: th.accent }}>{t(gr.title)}</h3>
              <p className="font-body text-sm text-white/80 leading-relaxed mt-4">{t(gr.text)}</p>
            </div>
          ))}
        </div>
        {p.sportNote && (
          <p data-testid="project-sport-note" className="mt-6 rounded-md border border-white/10 p-5 font-mono text-[10px] uppercase tracking-[0.1em] leading-relaxed text-white/60"
            style={{ backgroundColor: "var(--ms-surface)" }}>
            {t(p.sportNote)}
          </p>
        )}
      </Reveal>
    </section>
  );
};

const pad = (v) => String(v).padStart(2, "0");
const diffParts = (target) => {
  const d = Math.max(0, new Date(target) - new Date());
  return [Math.floor(d / 864e5), Math.floor(d / 36e5) % 24, Math.floor(d / 6e4) % 60, Math.floor(d / 1e3) % 60];
};
const CD_LABELS = [
  { fr: "Jours", en: "Days", es: "Días" },
  { fr: "Heures", en: "Hours", es: "Horas" },
  { fr: "Min", en: "Min", es: "Min" },
  { fr: "Sec", en: "Sec", es: "Seg" },
];

const TripCountdown = ({ p, th }) => {
  const { t } = useLang();
  const [, setTick] = React.useState(0);
  React.useEffect(() => {
    if (!p.countdowns) return undefined;
    const id = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(id);
  }, [p.countdowns]);
  if (!p.countdowns && !p.spots) return null;
  const booked = p.spots ? p.spots.total - p.spots.left : 0;
  return (
    <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16 md:pb-20" data-testid="trip-countdown">
      <Reveal>
        {p.spots && (
          <div data-testid="spots-counter" className="rounded-md border border-white/10 p-6 md:p-8 mb-6"
            style={{ backgroundColor: "var(--ms-surface)", borderLeft: `4px solid ${th.accent2 || th.accent}` }}>
            <div className="flex flex-wrap items-end justify-between gap-4">
              <div>
                <MonoTag className="blink" style={{ color: th.accent2 || th.accent }}>
                  ● {t({ fr: "Places limitées", en: "Limited spots", es: "Plazas limitadas" })}
                </MonoTag>
                <p className="font-display text-4xl md:text-5xl mt-2">
                  <span style={{ color: th.accent2 || th.accent }}>{p.spots.left}</span>
                  <span className="text-2xl md:text-3xl"> / {p.spots.total}</span>
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] mt-1">
                  {t({ fr: "places restantes — réservez vite", en: "spots left — book fast", es: "plazas restantes — reserva rápido" })}
                </p>
              </div>
              <div className="w-full md:w-1/2">
                <div className="h-2.5 rounded-full overflow-hidden" style={{ backgroundColor: "rgba(20,24,31,0.12)" }}>
                  <div className="h-full rounded-full" style={{ width: `${Math.round((booked / p.spots.total) * 100)}%`, backgroundColor: th.accent2 || th.accent }} />
                </div>
                <p className="font-mono text-[9px] uppercase tracking-[0.2em] mt-2 text-right">
                  {booked} {t({ fr: "places déjà réservées", en: "spots already booked", es: "plazas ya reservadas" })}
                </p>
              </div>
            </div>
          </div>
        )}
        {p.countdowns && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {p.countdowns.map((c, ci) => {
              const vals = diffParts(c.date);
              return (
                <div key={c.date} data-testid={`countdown-card-${ci}`} className="rounded-md border border-white/10 p-6 md:p-8"
                  style={{ backgroundColor: "var(--ms-surface)", borderTop: `3px solid ${th.accent}` }}>
                  <MonoTag style={{ color: th.accent }}>{t(c.label)}</MonoTag>
                  <p className="font-heading font-semibold text-sm mt-2">{t(c.sub)}</p>
                  <div className="grid grid-cols-4 gap-2 md:gap-3 mt-5">
                    {vals.map((v, i) => (
                      <div key={CD_LABELS[i].fr} className="rounded-md border border-white/10 py-4 md:py-5 text-center" style={{ backgroundColor: "var(--ms-bg)" }}>
                        <div className="font-display text-3xl md:text-4xl" style={{ color: th.accent }}>{pad(v)}</div>
                        <div className="font-mono text-[8px] md:text-[9px] uppercase tracking-[0.2em] mt-1">{t(CD_LABELS[i])}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Reveal>
    </section>
  );
};

// Generic project mini-site: theme, hero, fact sheet, objectives, CTA
export default function ProjectPage({ category }) {
  useLenis();
  const { slug } = useParams();
  const p = getProject(slug);
  if (!p || p.category !== category) return <Navigate to={`/${category}`} replace />;
  const th = p.theme;
  return (
    <MiniSite theme={th}>
      <ProjectHero p={p} th={th} category={category} />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-16 md:pb-20">
        <Reveal><FicheInfo fiche={p} accent={th.accent} /></Reveal>
      </section>

      <TripCountdown p={p} th={th} />

      <ObjectivesSection p={p} th={th} />
      <PriceTableSection p={p} th={th} />
      <FormulesSection p={p} th={th} />
      <NbaSection p={p} th={th} />
      <VisitsSection p={p} th={th} />
      <DetailsGrid p={p} th={th} />
      <DestinationsSection p={p} th={th} />
      <VideoSection p={p} th={th} />
      <StoriesSection p={p} th={th} />
      <GallerySection p={p} th={th} />

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-20 md:pb-28">
        <FaqBlock accent={th.accent} />
      </section>

      <ContactSection defaultProgram={p.name} accent={th.accent}
        programs={byCategory(category).map((x) => x.name)} />
    </MiniSite>
  );
}
